import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

/** CSS-driven, so nothing runs per frame; the cost is that a keyframe cannot be scrubbed, so no grab-to-spin. */

// MARK: Belt — a straight line of items looping forever

/** Each item carries its own trailing gap (`mr-*`/`mb-*`, not `gap-*`), or the belt jumps each lap. */
export function Belt({
	axis = 'x',
	speed,
	reverse,
	className,
	children,
}: {
	axis?: 'x' | 'y';
	/** px per second, whatever the item count. */
	speed: number;
	reverse?: boolean;
	className?: string;
	children: ReactNode;
}) {
	const trackRef = useRef<HTMLDivElement>(null);
	const copyRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const track = trackRef.current;
		const copy = copyRef.current;
		if (!track || !copy) return;
		const update = () => {
			const length = axis === 'x' ? copy.offsetWidth : copy.offsetHeight;
			track.style.animationDuration = `${Math.max(1, Math.round(length / speed))}s`;
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(copy);
		return () => ro.disconnect();
	}, [axis, speed]);

	const copyClass = cn('flex shrink-0', axis === 'y' && 'flex-col');

	return (
		<div
			ref={trackRef}
			className={cn('new-belt-item flex', axis === 'x' ? 'w-max' : 'flex-col', className)}
			// LONGHANDS: the `animation` shorthand resets `reverse` and the reduced-motion rule. Duration is the effect's.
			style={{
				animationName: axis === 'x' ? 'new-belt-x' : 'new-belt-y',
				animationTimingFunction: 'linear',
				animationIterationCount: 'infinite',
				animationDirection: reverse ? 'reverse' : 'normal',
			}}
		>
			<div ref={copyRef} className={copyClass}>
				{children}
			</div>
			<div className={copyClass}>{children}</div>
		</div>
	);
}

// MARK: Wheel

const COL_H = 576; // px — wheel viewport height
const CARD_W = 224; // px — card width (w-56)
const CARD_H = 96; // px — card height (h-24)
const SLOT_H = 112; // px — spacing between cards along the belt (COL_H / SLOT_H ≈ cards per run)
const ARC_AMPLITUDE = 96; // px the middle card bulges toward the center
const MIN_SCALE = 0.78; // scale of a card entering/leaving at top/bottom
const MAX_SCALE = 1.08; // scale of the card sitting at the arc's apex (center)
const MIN_OPACITY = 0.55; // opacity of a card at the far end of a run — depth, not "switched off"
const WHEEL_SPEED = 36; // px per second of belt travel
const RUN_INSET_RATIO = 0.11; // pull both runs this fraction of the width in from the edges
const MIN_ITEMS = 20; // fewest cards that fill both runs, so the two handoffs stay off-screen
/** Keyframe stops across each run's on-screen stretch, where the arc curves. */
const ARC_SAMPLES = 18;

/** Each run is half the belt, far taller than the viewport, so both handoffs happen off-screen. */
function wheelState(s: number, count: number) {
	const L = count * SLOT_H;
	const runLen = L / 2;
	const bp = L * (1 - s);
	const left = bp >= runLen;
	const yc = left ? COL_H / 2 - runLen / 2 + (bp - runLen) : COL_H / 2 - bp + runLen / 2;

	const u = Math.min(1, Math.max(0, yc / COL_H));
	const arc = Math.sin(Math.PI * u);
	const tuck = (1 - arc) * ARC_AMPLITUDE;
	// Percentages of the full-width rail, i.e. the wheel's width.
	const inset = RUN_INSET_RATIO * 100;
	const x = left ? `calc(${inset}% + ${tuck.toFixed(2)}px)` : `calc(${100 - inset}% - ${(CARD_W + tuck).toFixed(2)}px)`;

	return {
		x,
		y: yc - CARD_H / 2,
		scale: MIN_SCALE + arc * (MAX_SCALE - MIN_SCALE),
		opacity: MIN_OPACITY + arc * (1 - MIN_OPACITY),
	};
}

/** Generated per belt length: the run-to-viewport ratio decides how much of a lap the arc takes. */
const at = (s: number) => `${(s * 100).toFixed(4)}%`;

function wheelKeyframes(count: number) {
	const L = count * SLOT_H;
	const runLen = L / 2;
	const sOf = (bp: number) => 1 - bp / L;
	const bpLeft = (yc: number) => yc - COL_H / 2 + runLen / 2 + runLen;
	const bpRight = (yc: number) => COL_H / 2 + runLen / 2 - yc;

	const stops = new Set([0, 0.5, 1]);
	for (const bpOf of [bpLeft, bpRight]) {
		const ends = [sOf(bpOf(-CARD_H)), sOf(bpOf(COL_H + CARD_H))];
		const lo = Math.max(0, Math.min(...ends));
		const hi = Math.min(1, Math.max(...ends));
		for (let k = 0; k <= ARC_SAMPLES; k++) stops.add(lo + ((hi - lo) * k) / ARC_SAMPLES);
	}

	const sorted = [...stops].sort((a, b) => a - b);
	const name = `new-wheel-${count}`;
	const rail = sorted.map((s) => {
		const g = wheelState(s, count);
		return `${at(s)}{transform:translate3d(${g.x},${g.y.toFixed(2)}px,0)}`;
	});
	const card = sorted.map((s) => {
		const g = wheelState(s, count);
		return `${at(s)}{scale:${g.scale.toFixed(4)};opacity:${g.opacity.toFixed(4)}}`;
	});

	return {
		name,
		duration: L / WHEEL_SPEED,
		css: `@keyframes ${name}-rail{${rail.join('')}}@keyframes ${name}-card{${card.join('')}}`,
	};
}

/** Interleaved, since the runs sit half a belt apart: the top of the ranking shows on both sides. */
function beltOrder<T>(items: T[]): T[] {
	if (items.length === 0) return [];
	const filled =
		items.length < MIN_ITEMS
			? Array.from({ length: items.length * Math.ceil(MIN_ITEMS / items.length) }, (_, k) => items[k % items.length]!)
			: items;
	const half = Math.ceil(filled.length / 2);
	return filled.map((_, i) => filled[i < half ? i * 2 : (i - half) * 2 + 1] ?? filled[i]!);
}

/** Every card runs the same keyframes with its own negative `animation-delay`: no JavaScript after mount. */
export function Wheel<T>({
	items,
	renderItem,
	className,
}: {
	items: T[];
	renderItem: (item: T, i: number) => ReactNode;
	className?: string;
}) {
	const belt = beltOrder(items);
	const count = belt.length;
	if (count === 0) return <div className={cn('h-[576px]', className)} />;

	const { name, duration, css } = wheelKeyframes(count);
	const L = count * SLOT_H;

	return (
		<div
			className={cn(
				'relative h-[576px] w-full overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_14%,black_86%,transparent)] select-none',
				className,
			)}
		>
			<style href={name} precedence="medium" dangerouslySetInnerHTML={{ __html: css }} />
			{belt.map((item, i) => {
				// A quarter turn in, so item 0 opens at the right apex and item 1 at the left.
				const progress = 1 - ((((i * SLOT_H + L / 4) % L) + L) % L) / L;
				// Longhands: the shorthand would set `animation-play-state: running` inline and beat the
				// reduced-motion rule in theme.css.
				const timing = {
					animationDuration: `${duration.toFixed(3)}s`,
					animationTimingFunction: 'linear',
					animationDelay: `${(-progress * duration).toFixed(3)}s`,
					animationIterationCount: 'infinite',
				} as const;
				return (
					<div
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						className="new-belt-item absolute top-0 left-0 h-0 w-full"
						style={{ animationName: `${name}-rail`, ...timing }}
					>
						{/* `w-fit`: `scale` pivots on the element's own centre, so a full-width box drifts sideways. */}
						<div className="new-belt-item w-fit" style={{ animationName: `${name}-card`, ...timing }}>
							{renderItem(item, i)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
